import _ from 'lodash';
import autobind from 'autobind-decorator';
import {
	observable,
	action,
	reaction,
	computed,
	runInAction,
	extendObservable
} from 'mobx';

import { BaseStore, apiStore, userStore } from 'common/stores';
import config from 'client/shared/config';

import artistStore from './artist-store';
import playlistStore from './playlist-store';

const { routes } = config;

function removeBadChars (value) {
	return value
		.replace(/['|"|,|`]/g, '')
		.trim()
		.replace(/\s/g, '_');
}

function mapUntitledSongs (item) {
	if (_.isEmpty(item.title)) {

		item.title = 'Untitled';
	}

	return item;
}

class SongsStore extends BaseStore {
	apiRoute = routes.songs;

	@observable
	isUpLoading = false;

	@observable
	_songModalOpen = false;

	@observable
	currentUploadProgress = 0;

	@observable
	_activeSongRowID = null;

	constructor () {
		super();

		reaction(() => apiStore.data.songs, this.onNewData);
	}

	@computed
	get data () {
		return {
			song: this._data.item
		};
    }

    @computed
	get artistSongDirectory () {
		return removeBadChars(`${userStore.get('user.userName')}/${artistStore.get('artist.name')}`);
	}

	@computed
	get songs () {
		return this._items;
	}

	set songs (songs) {
		this._items = songs;
	}

	@computed
	get showNewSongAlbumForm () {
		return this.get('song.albumID') === 'new-album';
	}

	@computed
	get singlesList () {
		return this.songs.filter(({ albumID }) => albumID === 'singles');
	}

	@computed
	get fullPreviewList () {
		return _(this.songs)
			.filter(({ isFullPreview }) => isFullPreview)
			.sortBy('fullPreviewPosition')
			.value();
	}

	@computed
	get tracksList () {
		return _(this.songs)
			.filter(({ isFullPreview }) => isFullPreview !== true)
			.sortBy('createdAt')
			.value();
	}

	@computed
	get newSongs () {
		return this.songs
			.filter(({ isNew }) => isNew);
	}

	@computed
	get songModalOpen () {
		return this._songModalOpen;
	}

	set songModalOpen (val) {
		this._songModalOpen = val;
	}

	@computed
	get activeSongRowID () {
		return this._activeSongRowID;
	}

	set activeSongRowID (_id) {
		this._activeSongRowID = _id;
	}

	@observable
	_currentSongPlayback = observable.map({});

	@computed
	get currentSongPlayback () {
		return this._currentSongPlayback;
	}

	set currentSongPlayback (song) {
		song = _.isString(song) ? this.getSongByID(song) : song;
		this._currentSongPlayback.replace(song);
	}

	@autobind
	@action
	onNewData (songs) {
		this.songs = songs.map(mapUntitledSongs);
		this.setIsLoading(false);
	}

	@action
	setCurrentSong (currentSong) {
		this.currentSong = currentSong;
		playlistStore.currentPlaylist = null;
	}


	@action
	async updateSong ({ key, value, _id, save = true}) {
		await this.updateCollectionItem({ key, value, _id, save });
	}

	@action
	async updateFullPreviewPosition ({ _id, artistID }, position) {
		const route = routes.songFullPreviewPosition.replace(/\:_id/, _id);
		const { data } = await this.ax.put(route, { position, artistID });

		runInAction('Update after updating full prevoew position', () => {
			_.each(data, (updated) => {
				if (updated === null) {

					return;
				}

				const song = this.getSongByID(updated._id);
				song.updatedAt = updated.updatedAt;
				song.isFullPreview = updated.isFullPreview;
				song.fullPreviewPosition = updated.fullPreviewPosition;
			});
		});
	}

	@action
	async removeFullPreview ({ _id }) {
		const route = routes.songFullPreviewRemove.replace(/\:_id/, _id);
		const { data: updated } = await this.ax.put(route);
		runInAction('Update after removing from full previews', () => {
			if (updated === null) {

				return;
			}

			const song = this.getSongByID(updated._id);
			song.updatedAt = updated.updatedAt;
			song.isFullPreview = updated.isFullPreview;
			song.fullPreviewPosition = updated.fullPreviewPosition;
		});
	}

	// @action
	// async saveSong () {
	// 	const route = routes.songUpload.replace(/\:_id/, artistStore.get('artist._id'));
	// 	const formData = new FormData();
	// 	const s3Path = removeBadChars(`${userStore.get('user.userName')}/${artistStore.get('artist.name')}`);
	// 	const opts = {
	// 		onUploadProgress: (progressEvent) => {
	// 			this.currentUploadProgress = progressEvent.loaded / progressEvent.total;
	// 		}
	// 	};

	// 	formData.append('s3Path', s3Path);

	// 	['song', 'album'].forEach((objKey) => {
	// 		const keys = _.keys(this.get(objKey));

	// 		keys.forEach((key) => {
	// 			const fullKey = `${objKey}.${key}`;
	// 			formData.append(`${objKey}.${key}`, this.get(fullKey));
	// 		});
	// 	});

	// 	try {

	// 		const { data } = await this.ax.put(route, formData, opts);
	// 		runInAction('Update after saving song', () => {
	// 			this._items.push(data);
	// 		});
	// 	} catch (err) {

	// 		console.error('[ERROR SongsStore.saveSong]', err);
	// 	}
	// }

	@action
	async saveSong (song, artist, opts = {}) {
		const formData = new FormData();
		const options = {
			...opts,
			method: 'put',
			route: config.getRoute('song').replace(/\:id/, song._id)
		};

		try {

			return await this.saveItem(song, options);
		} catch (err) {

			console.error('[ERROR SongsStore.saveSong]', err);
		}
	}
	
	@action
	async uploadSongs (songs = []) {
		const artistID = artistStore.get('artist._id')
		const route = routes.songUpload.replace(/\:_id/, artistID);
		const formData = new FormData();
		const s3Path = this.artistSongDirectory;
		const opts = {
			onUploadProgress: (progressEvent) => {
				this.currentUploadProgress = progressEvent.loaded / progressEvent.total;
			}
		};

		formData.append('s3Path', s3Path);
		songs.forEach((song) => formData.append('songs[]', song, song.name));

		this.isUpLoading = true;
		try {

			const { data } = await this.ax.put(route, formData, opts);
			runInAction('Update after uploading songs', () => {
				this._items = data;
			});
		} catch (err) {

			console.error('[ERROR SongsStore.uploadSongs]', err);
		} finally {

			this.isUpLoading = false;
		}
	}

	@action
	async fetchSongsByArtist (artistID) {
		console.log("ARTIST ID:");
		console.log(artistID);
		const route = routes.songsByArtist.replace(/:artistID/, artistID);

		this.setIsLoading(true);

		try {

			const { data } = await this.ax.get(route);
			runInAction('Update songs', () => {
				this.songs = data.map(mapUntitledSongs);
			});

		} catch (err) {

			console.log('[ERROR SongsStore.fetchItemsByArtist]', err);
		} finally {

			this.setIsLoading(false);
		}
	}

	getSongIndex (song) {
		return this.getItemIndex(song);
	}

	getSongsByArtistID (_id) {
		return this.songs
			.filter(({ artistID }) => artistID === _id);
	}

	getSongByID (value) {

		if (_.isString(value)) {

			return _.first(this._items.filter(({ _id }) => _id === value));
		}

		if (_.isObject(value) && _.isArray(value.slice())) {

			return _(value.slice())
				.map((_id) => this.getSongByID(_id))
				.compact()
				.value();
		}

		return null;
	}

	getSongTitle (song) {
		const explicit = song.explicit ? ' (explicit)' : '';
		return `${song.title}${explicit}`;
	}

	formatDuration ({ duration }) {
		if (window.isNaN(duration) || duration === 0) {

			return 0;
		}

		const seconds = duration % 60;

		return `${Math.floor(duration / 60)}:${seconds < 10 ? `0${seconds}` : seconds}`;
	}
}

const songsStore = new SongsStore();

export default songsStore;
